import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    input,
    OnInit,
    signal,
    untracked,
    ViewChild,
    viewChild,
} from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { CommonModule } from "@angular/common";
import { S25LoadingInlineModule } from "../../s25-loading/s25.loading.inline.module";
import { S25TableModule } from "../../s25-table/s25.table.module";
import { S25TableComponent } from "../../s25-table/s25.table.component";
import { Table } from "../../s25-table/Table";
import { GenericTableSimpleSelectComponent } from "../../s25-table/generics/generic.table.simple.select.component";
import { S25MultiselectModule } from "../../s25-multiselect/s25-multiselect.module";
import { S25MultiselectSearchCriteriaComponent } from "../../s25-multiselect/multiselects/s25.multiselect.search.criteria.component";
import { MultiselectModelI } from "../../s25-multiselect/s25.multiselect.component";
import { AccessLevel, AccessLevels, AccessLevelValue } from "../../../pojo/Fls";
import { S25ItemI } from "../../../pojo/S25ItemI";
import { RoleRights } from "./role.rights";
import {
    accessLevelSelectOptionsForRight,
    accessLevelSelectOptionsNonDemoting,
    accessLevelShortLabel,
    SeSecurityBaselineConfigStore,
} from "./se.security.baseline.config.store";
import { SeSecurityOrgConfigStore } from "./se.security.org.config.store";
import { SeSecurityOrgContactGrantsStore } from "./se.security.org.contact.grants.store";
import { SePermissionDiagnosticComponent } from "./se.security.permission.diagnostic.component";
import { ContactRole, ContactRoleId } from "../../../services/organization.service";
import { S25Util } from "../../../util/s25-util";
import { GenericTableButtonComponent } from "../../s25-table/generics/generic.table.button.component";
import { GenericTableFadePreviewComponent } from "../../s25-table/generics/generic.table.fade.preview.component";

type ConfigViewMode = "matrix" | "byRole";

@TypeManagerDecorator("s25-ng-se-security")
@Component({
    selector: "s25-ng-se-security",
    template: `
        <div class="se-security-config">
            <div class="c-padding--double">
                <h1>{{ pageTitle() }}</h1>
                <p class="description">
                    {{ pageDescription() }}
                </p>

                @if (store.loading()) {
                    <s25-loading-inline [model]="{ text: 'Loading configuration...' }" />
                }

                @if (!store.loading() && store.error()) {
                    <div class="se-security-config__error" role="alert">{{ store.error() }}</div>
                }

                @if (!store.loading()) {
                    @if (store.rights().length === 0) {
                        <p class="ngFinePrint">No Connect25 rights are configured.</p>
                    } @else {
                        @if (readOnly()) {
                            <p class="ngFinePrint">
                                You have view-only access to this page.Editing role rights and contact grants requires
                                full access on “Organization security configuration”.
                            </p>
                        } @else {
                            <div class="se-security-config__toolbar card">
                                <div class="se-security-config__toolbar-actions">
                                    <div class="se-security-config__view-toggle" role="group" aria-label="Layout">
                                        <button
                                            type="button"
                                            class="aw-button"
                                            [class.aw-button--primary]="viewMode() === 'matrix'"
                                            (click)="setViewMode('matrix')"
                                        >
                                            All roles &amp; rights
                                        </button>
                                        <button
                                            type="button"
                                            class="aw-button"
                                            [class.aw-button--primary]="viewMode() === 'byRole'"
                                            (click)="setViewMode('byRole')"
                                        >
                                            By role
                                        </button>
                                    </div>
                                </div>

                                @if (viewMode() === "byRole" && rolePickerBean) {
                                    <s25-ng-multiselect-search-criteria
                                        [type]="'organizationRoles'"
                                        [singleSelect]="true"
                                        [modelBean]="rolePickerBean"
                                        [selectedItems]="rolePickerBean.selectedItems ?? []"
                                        [showResults]="true"
                                        [popoverOnBody]="true"
                                        [useSecurity]="true"
                                        (close)="onRolePickerClose($event)"
                                    />
                                }
                            </div>

                            <div class="se-security-config__busy-region" [attr.aria-busy]="anySaving() ? 'true' : null">
                                @if (viewMode() === "matrix") {
                                    @if (store.matrixRoleColumns().length === 0) {
                                        <p class="ngFinePrint">
                                            No organization roles are available to show in the matrix.
                                        </p>
                                    } @else {
                                        <p class="ngFinePrint se-security-config__matrix-hint">
                                            {{ matrixHint() }}
                                        </p>
                                        <div class="se-security-matrix-wrap">
                                            <s25-ng-table
                                                #matrixTable
                                                [dataSource]="matrixTableDataSource"
                                                [rowSortable]="false"
                                                [columnSortable]="true"
                                                [hasFilter]="true"
                                                [hasRefresh]="true"
                                                [hasColumnChooser]="true"
                                                [unlimitedWidth]="true"
                                                [caption]="matrixCaption()"
                                            />
                                        </div>
                                    }
                                }

                                @if (viewMode() === "byRole") {
                                    <div class="se-security-config__by-role-shell">
                                        <div class="se-security-config__by-role-main">
                                            @if (store.contactRoles().length === 0) {
                                                <p class="ngFinePrint">
                                                    No contact roles are assigned to any Connect25 right yet.
                                                </p>
                                            } @else if (store.selectedRoleId() !== null) {
                                                <s25-ng-table
                                                    #roleTable
                                                    [dataSource]="byRoleTableDataSource"
                                                    [rowSortable]="false"
                                                    [columnSortable]="true"
                                                    [hasFilter]="true"
                                                    [hasRefresh]="true"
                                                    [caption]="byRoleCaption()"
                                                />
                                            } @else {
                                                <p class="ngFinePrint">
                                                    Select a role above to view and edit rights for that role.
                                                </p>
                                            }
                                        </div>
                                    </div>
                                }

                                @if (orgId() != null) {
                                    <div class="card se-security-config__contact-section">
                                        <h2 class="se-security-config__contact-section-title">
                                            Contact-specific rights
                                        </h2>
                                        <p class="ngFinePrint">
                                            Extra rights for an individual user in this organization (per contact, per
                                            org). Grants can be assigned to any user, not only organization members.
                                            This is in addition to role and organization defaults.
                                        </p>

                                        @if (contactGrantsStore.orgGrantIndexLoading()) {
                                            <s25-loading-inline
                                                [model]="{ text: 'Loading users with individual grants…' }"
                                            />
                                        }
                                        @if (contactGrantsStore.orgGrantIndexError()) {
                                            <div class="se-security-config__error" role="alert">
                                                {{ contactGrantsStore.orgGrantIndexError() }}
                                            </div>
                                        }

                                        @if (
                                            !contactGrantsStore.orgGrantIndexLoading() &&
                                            orgGrantSummaries().length === 0
                                        ) {
                                            <p class="ngFinePrint">
                                                No contacts have individual grants on this organization.
                                            </p>
                                        }
                                        @if (orgGrantSummaries().length > 0) {
                                            <div class="se-security-contact-summary-wrap">
                                                <s25-ng-table
                                                    #orgGrantSummaryTable
                                                    [dataSource]="orgGrantSummaryTableDataSource"
                                                    [rowSortable]="false"
                                                    [caption]="'Users with individual grants'"
                                                />
                                            </div>
                                        }

                                        @if (!readOnly()) {
                                            <label class="se-security-config__contact-member-label">
                                                <span>User</span>
                                                @if (userPickerBean) {
                                                    <s25-ng-multiselect-search-criteria
                                                        #userPicker
                                                        [type]="'contacts'"
                                                        [buttonText]="'Select contact'"
                                                        [modelBean]="userPickerBean"
                                                        [selectedItems]="userPickerBean.selectedItems ?? []"
                                                        [showResults]="true"
                                                        [popoverOnBody]="true"
                                                        [useSecurity]="false"
                                                        [singleSelect]="true"
                                                        (close)="onUserPickerClose($event)"
                                                    />
                                                }
                                                <button
                                                    type="button"
                                                    class="aw-button se-security-config__clear-user"
                                                    (click)="clearUserPicker()"
                                                >
                                                    Clear user
                                                </button>
                                            </label>
                                        }

                                        @if (selectedContactContId() != null) {
                                            @if (contactGrantsStore.loading()) {
                                                <s25-loading-inline [model]="{ text: 'Loading contact rights…' }" />
                                            }
                                            @if (contactGrantsStore.error()) {
                                                <div class="se-security-config__error" role="alert">
                                                    {{ contactGrantsStore.error() }}
                                                </div>
                                            }
                                            @if (
                                                !contactGrantsStore.loading() &&
                                                contactGrantsStore.definitions().length &&
                                                !readOnly()
                                            ) {
                                                <div class="se-security-contact-table-wrap">
                                                    <s25-ng-table
                                                        #contactGrantsTable
                                                        [dataSource]="contactGrantsTableDataSource"
                                                        [rowSortable]="false"
                                                        [caption]="'Individual grants for selected user'"
                                                    />
                                                </div>
                                            }
                                        }
                                    </div>
                                }
                            </div>
                        }
                    }
                }
            </div>
            <s25-ng-se-permission-diagnostic [orgId]="orgId()" />
        </div>
    `,
    styles: `
        .se-security-config {
            .description {
                color: var(--color--text-muted);
                margin-bottom: 2rem;
            }

            .se-security-config__matrix-hint {
                margin-bottom: 0.75rem;
            }

            .se-security-config__autosave-status {
                flex: 1 1 12rem;
                min-width: 0;
                font-size: 0.95rem;
                color: var(--color--text-muted);
                font-weight: 600;
            }

            .se-security-config__toolbar-actions {
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                justify-content: flex-end;
                gap: 1rem;
                flex: 1 1 auto;
            }

            .se-security-config__busy-region {
                position: relative;
            }

            .card {
                background: var(--color--surface-bg);
                border: 1px solid var(--color--border);
                border-radius: 8px;
                padding: 1.5rem;
                margin-bottom: 1.5rem;
                box-shadow: 0 2px 4px var(--color--table-border);
            }

            .se-security-config__toolbar {
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                justify-content: space-between;
                gap: 1rem;
                margin-bottom: 1rem;
            }

            .se-security-config__view-toggle {
                display: flex;
                flex-wrap: wrap;
                gap: 0.5rem;
            }

            .se-security-config__by-role-shell {
                display: flex;
                flex-wrap: wrap;
                align-items: flex-start;
                gap: 1.25rem;
            }

            .se-security-config__role-sidebar {
                flex: 0 0 auto;
                width: min(22rem, 100%);
                margin-bottom: 0;
            }

            .se-security-config__by-role-main {
                flex: 1 1 280px;
                min-width: 0;
            }

            .se-security-config__role-label {
                display: flex;
                flex-direction: column;
                gap: 0.35rem;
                font-weight: 600;
                margin: 0;

                s25-ng-multiselect-search-criteria {
                    max-width: none;
                }
            }

            @media (max-width: 768px) {
                .se-security-config__by-role-shell {
                    flex-direction: column;
                }

                .se-security-config__role-sidebar {
                    width: 100%;
                }
            }

            .se-security-config__error {
                padding: 0.75rem 1rem;
                margin-bottom: 1rem;
                border-radius: 6px;
                background: var(--color--surface-bg-error);
                color: var(--color--danger);
            }

            .se-security-matrix-wrap {
                --se-sec-matrix-cat-w: 120px;
            }

            .se-security-config__contact-section-title {
                margin-top: 0;
                margin-bottom: 0.5rem;
                font-size: 1.15rem;
            }

            .se-security-config__contact-member-label {
                display: flex;
                flex-direction: column;
                gap: 0.35rem;
                font-weight: 600;
                margin-bottom: 1rem;
                max-width: 28rem;
            }

            .se-security-config__clear-user {
                align-self: flex-start;
            }

            .se-security-contact-summary-wrap {
                margin-bottom: 1rem;
            }

            .se-security-contact-table-wrap {
                margin-top: 0.5rem;
            }
        }

        :host ::ng-deep .se-security-config td.se-security-config__description-cell {
            white-space: pre-line;
        }
    `,
    standalone: true,
    imports: [
        CommonModule,
        S25LoadingInlineModule,
        S25TableModule,
        S25MultiselectModule,
        SePermissionDiagnosticComponent,
    ],
    providers: [SeSecurityBaselineConfigStore, SeSecurityOrgConfigStore, SeSecurityOrgContactGrantsStore],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SeSecurityComponent implements OnInit {
    /** When set, loads org overrides; only elevations allowed (no demotion). */
    readonly orgId = input<number | undefined>(undefined);

    /** Organization members (contact + role) for the contact-specific rights picker. */
    readonly orgMembers = input<ContactRole[]>([]);

    /** One row per contact; callers may pass duplicate contacts when a user has multiple roles. */
    protected readonly distinctOrgMembers = computed(() =>
        S25Util.array.unique(this.orgMembers(), (contactRole) => contactRole.itemId),
    );

    protected readonly memberNamesByContId = computed(() => {
        const nameMap = new Map<number, string>();
        for (const member of this.distinctOrgMembers()) {
            const contactId = Number(member.itemId);
            if (!Number.isFinite(contactId)) continue;
            nameMap.set(contactId, member.itemName);
        }
        return nameMap;
    });

    protected readonly memberRolesByContId = computed(() => {
        const roleNamesByContact = new Map<number, string[]>();
        for (const member of this.orgMembers()) {
            const contactId = Number(member.itemId);
            if (!Number.isFinite(contactId)) continue;
            const roleName = member.role?.itemName?.trim();
            if (!roleName) continue;
            const existing = roleNamesByContact.get(contactId) ?? [];
            if (!existing.includes(roleName)) {
                existing.push(roleName);
            }
            roleNamesByContact.set(contactId, existing);
        }
        for (const roleNames of roleNamesByContact.values()) {
            roleNames.sort((left, right) => left.localeCompare(right));
        }
        return roleNamesByContact;
    });

    protected readonly orgGrantSummaries = computed(() =>
        this.contactGrantsStore.orgGrantSummaries(this.store.rights()),
    );

    /** When true, hides matrix / role / contact editors; diagnostic remains visible. */
    readonly readOnly = input(false);

    private readonly baselineStore = inject(SeSecurityBaselineConfigStore);
    protected readonly orgStore = inject(SeSecurityOrgConfigStore);
    protected readonly contactGrantsStore = inject(SeSecurityOrgContactGrantsStore);

    protected get store(): SeSecurityBaselineConfigStore | SeSecurityOrgConfigStore {
        return this.orgId() != null ? this.orgStore : this.baselineStore;
    }

    protected readonly pageTitle = computed(() =>
        this.orgId() != null ? "Organization role security" : "Connect25 Security Configuration",
    );

    protected readonly pageDescription = computed(() =>
        this.orgId() != null
            ? "System-wide defaults apply to all organizations. Per-cell edits cannot lower access. Use the “Apply to All” column to set every visible role column in that row at once (elevations and demotions are attempted; unsuccessful cells are skipped)."
            : "System default role rights for Connect25. Use the matrix to see which roles have which access on each right, or switch to a single role to edit in a compact list. Changes save when you change an access level.",
    );

    protected readonly matrixHint = computed(() =>
        this.orgId() != null
            ? "Lower access options are disabled when they would reduce an existing grant (per-cell). Each control notes the system default for that role and right. “Apply to All” sets every visible role column in that row when you change the level. Use Columns to show or hide role columns; Category and Right stay visible."
            : "“Apply to All” sets every visible role column in that row when you change the level. Use Columns to show or hide role columns. Category and Right stay visible.",
    );

    protected readonly matrixCaption = computed(() =>
        this.orgId() != null ? "Organization role rights (matrix)" : "Connect25 rights by role (matrix)",
    );

    protected readonly byRoleCaption = computed(() =>
        this.orgId() != null ? "Organization rights for selected role" : "Connect25 rights for selected role",
    );

    /** Main config store save flag (baseline vs org, never both active for one screen). */
    protected readonly configSaving = computed(() =>
        this.orgId() != null ? this.orgStore.saving() : this.baselineStore.saving(),
    );

    /** True while either main config or contact grants persist to the server. */
    protected readonly anySaving = computed(
        () => this.configSaving() || (this.orgId() != null && this.contactGrantsStore.saving()),
    );

    @ViewChild("matrixTable") private matrixTable?: S25TableComponent;
    @ViewChild("roleTable") private roleTable?: S25TableComponent;
    @ViewChild("contactGrantsTable") private contactGrantsTable?: S25TableComponent;
    @ViewChild("orgGrantSummaryTable") private orgGrantSummaryTable?: S25TableComponent;
    private readonly userPicker = viewChild<S25MultiselectSearchCriteriaComponent>("userPicker");

    /** Dedupes roster-name merges when the parent rebinds the same members array each CD cycle. */
    private mergedRosterKey: string | null = null;

    constructor() {
        effect(() => {
            const orgId = this.orgId();
            if (orgId == null) {
                this.mergedRosterKey = null;
                return;
            }

            const members = this.orgMembers();
            const rosterKey = `${orgId}|${members
                .map((member) => `${Number(member.itemId)}:${member.itemName}`)
                .sort()
                .join("\n")}`;
            if (rosterKey === this.mergedRosterKey) return;
            this.mergedRosterKey = rosterKey;

            if (!members.length) return;

            untracked(() => {
                const namesChanged = this.contactGrantsStore.mergeOrgGrantContactNames(this.memberNamesByContId());
                if (namesChanged) {
                    queueMicrotask(() => void this.orgGrantSummaryTable?.refresh(true));
                }
            });
        });
    }

    /** Selected user for contact-specific rights (contact id). */
    protected readonly selectedContactContId = signal<number | null>(null);
    protected readonly selectedContactRoleId = signal<number | null>(null);

    protected readonly viewMode = signal<ConfigViewMode>("byRole");

    /** Search-criteria multiselect model for picking the active contact role */
    protected rolePickerBean: MultiselectModelI | null = null;
    protected userPickerBean: MultiselectModelI | null = null;

    readonly byRoleTableDataSource: Table.Unpaginated = {
        type: "unpaginated",
        columns: [
            { id: "category", header: "Category", sortable: true },
            { id: "right", header: "Right", sortable: true },
            {
                id: "summary",
                header: "Description",
                cells: { className: "se-security-config__description-cell" },
            },
            {
                id: "access",
                header: "Access level",
                sortable: false,
                width: "min-content",
            },
        ],
        dataSource: async () => ({ rows: this.buildByRoleTableRows() }),
    };

    readonly matrixTableDataSource: Table.Unpaginated = {
        type: "unpaginated",
        columns: [],
        dataSource: async () => ({ rows: this.buildMatrixTableRows() }),
    };

    readonly contactGrantsTableDataSource: Table.Unpaginated = {
        type: "unpaginated",
        columns: [
            { id: "category", header: "Category", sortable: true },
            { id: "right", header: "Right", sortable: true },
            { id: "summary", header: "Description" },
            {
                id: "access",
                header: "Individual grant",
                sortable: false,
                width: "min-content",
            },
        ],
        dataSource: async () => ({ rows: this.buildContactGrantTableRows() }),
    };

    readonly orgGrantSummaryTableDataSource: Table.Unpaginated = {
        type: "unpaginated",
        columns: [
            { id: "contact", header: "User", sortable: true, minWidth: 160 },
            {
                id: "roles",
                header: "Roles",
                sortable: true,
                minWidth: 140,
                cells: { component: GenericTableFadePreviewComponent },
            },
            {
                id: "grants",
                header: "Individual grants",
                sortable: false,
                minWidth: 200,
                cells: { component: GenericTableFadePreviewComponent },
            },
        ],
        dataSource: async () => ({ rows: this.buildOrgGrantSummaryTableRows() }),
    };

    async ngOnInit(): Promise<void> {
        const oid = this.orgId();
        if (oid != null) {
            this.orgStore.setOrgId(oid);
        }
        await this.store.load();
        this.syncMatrixTableColumns();
        this.rolePickerBean = this.buildRolePickerModel();
        this.userPickerBean = this.buildUserPickerModel();
        this.selectedContactContId.set(null);
        this.selectedContactRoleId.set(null);
        if (oid != null) {
            this.contactGrantsStore.setOrgContext(oid);
            await this.contactGrantsStore.loadOrgGrantIndex(this.memberNamesByContId());
        } else {
            this.contactGrantsStore.clear();
        }
        setTimeout(() => {
            void this.matrixTable?.refresh(true);
            void this.roleTable?.refresh(true);
            void this.orgGrantSummaryTable?.refresh(true);
        }, 0);
    }

    protected onUserPickerClose(selected: S25ItemI[] | undefined): void {
        const items = selected ?? [];
        const first = items[0];
        const contactId = first?.itemId != null ? Number(first.itemId) : null;
        if (contactId == null || !Number.isFinite(contactId)) {
            void this.clearUserSelection();
            return;
        }
        void this.selectUserForGrants(contactId, first.itemName);
    }

    protected clearUserPicker(): void {
        void this.clearUserSelection();
    }

    private async clearUserSelection(): Promise<void> {
        this.selectedContactContId.set(null);
        this.selectedContactRoleId.set(null);
        this.contactGrantsStore.clearSelectedContact();
        this.syncUserPickerSelection([]);
    }

    private syncUserPickerSelection(selected: S25ItemI[]): void {
        if (!this.userPickerBean) return;

        const selectedCopy = [...selected];
        this.userPickerBean.selectedItems = selectedCopy;

        const picker = this.userPicker();
        if (!picker) return;

        picker.modelBean.selectedItems = selectedCopy;
        picker.value.set(selectedCopy);
        picker.refresh();
    }

    private async selectUserForGrants(contactId: number, displayName?: string): Promise<void> {
        const oid = this.orgId();
        if (oid == null || !Number.isFinite(contactId)) return;

        this.selectedContactContId.set(contactId);
        const member = this.distinctOrgMembers().find((contactRole) => Number(contactRole.itemId) === contactId);
        const roleId = member?.role?.itemId ?? null;
        this.selectedContactRoleId.set(roleId);

        const resolvedName = displayName ?? member?.itemName;
        this.contactGrantsStore.rememberContactDisplayName(contactId, resolvedName);

        const selectedItem: S25ItemI = {
            itemId: contactId,
            itemName: resolvedName ?? `Contact ${contactId}`,
        };
        this.syncUserPickerSelection([selectedItem]);

        this.contactGrantsStore.setContext(oid, contactId, roleId);
        await this.contactGrantsStore.load();
        queueMicrotask(() => {
            void this.contactGrantsTable?.refresh(true);
            void this.orgGrantSummaryTable?.refresh(true);
        });
    }

    private buildOrgGrantSummaryTableRows(): Table.Row[] {
        const roleNamesByContact = this.memberRolesByContId();
        return this.orgGrantSummaries().map((summary) => {
            const grantsText = summary.rightLabels.join(", ");
            const roleNames = roleNamesByContact.get(summary.contId) ?? [];
            const rolesText = roleNames.length ? roleNames.join(", ") : "—";
            return {
                id: `ogr_${summary.contId}`,
                name: summary.displayName,
                data: {
                    contId: summary.contId,
                    displayName: summary.displayName,
                },
                cells: {
                    contact: {
                        component: GenericTableButtonComponent,
                        inputs: {
                            label: summary.displayName,
                            type: "link",
                            onClick: () => this.selectUserForGrants(summary.contId, summary.displayName),
                        },
                        textValue: summary.displayName,
                        sortValue: summary.displayName,
                        filterValue: summary.displayName,
                    } satisfies Table.Cell<GenericTableButtonComponent>,
                    roles: {
                        textValue: rolesText,
                        sortValue: rolesText,
                        filterValue: roleNames.join(" "),
                        inputs: {
                            cell: { text: rolesText },
                        },
                    },
                    grants: {
                        textValue: grantsText,
                        filterValue: summary.rightLabels.join(" "),
                        inputs: {
                            cell: { text: grantsText },
                        },
                    },
                },
            };
        });
    }

    private buildUserPickerModel(): MultiselectModelI | null {
        const oid = this.orgId();
        if (oid == null) return null;
        return {
            uuid: `se-security-contact-picker-org-${oid}`,
            singleSelect: true,
            title: "Contacts",
            selectedItems: [],
        };
    }

    protected setViewMode(mode: ConfigViewMode): void {
        this.viewMode.set(mode);
        setTimeout(() => {
            if (mode === "matrix") {
                this.syncMatrixTableColumns();
                void this.matrixTable?.refresh(true);
            } else {
                void this.roleTable?.refresh(true);
            }
        }, 0);
    }

    private syncMatrixTableColumns(): void {
        const roles = this.store.matrixRoleColumns();
        this.matrixTableDataSource.columns = [
            { id: "category", header: "Category", sortable: true, permanent: true },
            { id: "right", header: "Right", sortable: true, permanent: true },
            { id: "summary", header: "Description", sortable: false, minWidth: 200 },
            {
                id: "applyToAll",
                header: "Apply to All",
                sortable: false,
                permanent: true,
                width: "min-content",
                align: "center",
                export: false,
            },
            ...roles.map((r) => ({
                id: `r_${r.id}`,
                header: r.name,
                sortable: false,
                width: "min-content" as const,
                align: "center" as const,
            })),
        ];
    }

    private buildRolePickerModel(): MultiselectModelI | null {
        if (this.store.contactRoles().length === 0) return null;
        const roleId = this.store.selectedRoleId();
        const roles = this.store.contactRoles();
        const selectedRole = roleId != null ? roles.find((r) => r.id === roleId) : undefined;
        return {
            uuid:
                this.orgId() != null ? `se-security-role-picker-org-${this.orgId() ?? ""}` : "se-security-role-picker",
            singleSelect: true,
            title: "Roles",
            selectedItems: selectedRole ? [{ itemId: selectedRole.id, itemName: selectedRole.name }] : [],
        };
    }

    protected onRolePickerClose(selected: S25ItemI[] | undefined): void {
        const items = selected ?? [];
        const first = items[0];
        if (first?.itemId != null) {
            this.store.selectRole(Number(first.itemId));
        } else {
            this.store.selectRole(null);
        }
        if (this.rolePickerBean) {
            this.rolePickerBean.selectedItems = [...items];
        }
        queueMicrotask(() => void this.roleTable?.refresh(true));
    }

    private buildByRoleTableRows(): Table.Row[] {
        const roleId = this.store.selectedRoleId();
        if (roleId === null) return [];

        return this.store.rights().map((def) => {
            const level = this.store.levelForSelectedRole(def.rightName);
            return {
                id: def.rightName,
                name: `${def.displayName} ${def.category}`,
                data: { rightName: def.rightName as string },
                cells: {
                    category: { text: def.category, sortValue: def.category, filterValue: def.category },
                    right: { text: def.displayName, filterValue: def.displayName },
                    summary: {
                        text: def.readDescription
                            ? `Read: ${def.readDescription}\nFull control: ${def.description}`
                            : def.description,
                        filterValue: `${def.readDescription ?? ""} ${def.description}`,
                    },
                    access: {
                        className: accessLevelCellClass(level),
                        component: GenericTableSimpleSelectComponent,
                        inputs: {
                            modelValue: level,
                            options: this.selectOptionsForByRole(def),
                            infoMsg: this.orgCellInfoForByRole(def),
                        },
                        outputs: {
                            modelValueChange: (value: string | number) => {
                                void this.onByRoleAccessChange(def, value as AccessLevel);
                            },
                        },
                    },
                },
            };
        });
    }

    private buildContactGrantTableRows(): Table.Row[] {
        const defs = this.contactGrantsStore.definitions();
        if (!defs.length) return [];

        return defs.map((def) => {
            const stored = this.contactGrantsStore.individualLevel(def.rightName);
            return {
                id: `cg_${def.rightName}`,
                name: `${def.displayName} ${def.category}`,
                data: { rightName: def.rightName as string },
                cells: {
                    category: { text: def.category, sortValue: def.category, filterValue: def.category },
                    right: { text: def.displayName, filterValue: def.displayName },
                    summary: {
                        text: def.readDescription ? `${def.readDescription} — ${def.description}` : def.description,
                        filterValue: `${def.readDescription ?? ""} ${def.description}`,
                    },
                    access: {
                        className: accessLevelCellClass(stored),
                        component: GenericTableSimpleSelectComponent,
                        inputs: {
                            modelValue: stored,
                            options: this.selectOptionsForContactGrant(def),
                            infoMsg: this.contactGrantCellInfo(def),
                        },
                        outputs: {
                            modelValueChange: (value: string | number) => {
                                void this.onContactGrantChange(def, value as AccessLevel);
                            },
                        },
                    },
                },
            };
        });
    }

    private selectOptionsForContactGrant(def: RoleRights.RightWithRolesConfig) {
        const rid = this.selectedContactRoleId();
        if (rid == null || rid === ContactRoleId.None) {
            return accessLevelSelectOptionsForRight(def);
        }
        const eff = this.orgStore.levelForRole(def.rightName, rid);
        return accessLevelSelectOptionsNonDemoting(def, eff).map((o) =>
            o.value === AccessLevels.None ? { ...o, disabled: false } : o,
        );
    }

    private contactGrantCellInfo(def: RoleRights.RightWithRolesConfig): string | undefined {
        const rid = this.selectedContactRoleId();
        if (rid == null || rid === ContactRoleId.None) {
            return "Organization + role: no role — individual grant only.";
        }
        const orgEff = this.orgStore.levelForRole(def.rightName, rid);
        return `Organization + role: ${accessLevelShortLabel(orgEff)}`;
    }

    private async onContactGrantChange(def: RoleRights.RightWithRolesConfig, value: AccessLevel): Promise<void> {
        await this.contactGrantsStore.setIndividualLevel(def.rightName, value);
        queueMicrotask(() => {
            void this.contactGrantsTable?.refresh(true);
            void this.orgGrantSummaryTable?.refresh(true);
        });
    }

    private buildMatrixTableRows(): Table.Row[] {
        const roles = this.store.matrixRoleColumns();
        const visibleRoleIds = this.visibleMatrixRoleColumnIds();
        return this.store.rights().map((def) => {
            const cells: Table.Row["cells"] = {
                category: { text: def.category, sortValue: def.category, filterValue: def.category },
                right: { text: def.displayName, filterValue: def.displayName },
                summary: {
                    text: def.readDescription ? `${def.readDescription} — ${def.description}` : def.description,
                    filterValue: `${def.readDescription ?? ""} ${def.description}`,
                },
            };

            if (!visibleRoleIds.length) {
                cells.applyToAll = {
                    text: "—",
                    filterValue: "apply to all",
                };
            } else {
                const applyLevel = this.matrixApplyToAllDisplayLevel(def, visibleRoleIds);
                cells.applyToAll = {
                    className: accessLevelCellClass(applyLevel),
                    component: GenericTableSimpleSelectComponent,
                    inputs: {
                        modelValue: applyLevel,
                        options: this.selectOptionsForMatrixApplyToAll(def),
                        infoMsg: this.matrixApplyToAllInfoMsg(def, visibleRoleIds),
                    },
                    outputs: {
                        modelValueChange: (value: string | number) => {
                            void this.onMatrixApplyToAllChange(def, value as AccessLevel);
                        },
                    },
                };
            }

            for (const role of roles) {
                const colId = `r_${role.id}` as const;
                const level = this.store.levelForRole(def.rightName, role.id);
                cells[colId] = {
                    className: accessLevelCellClass(level),
                    component: GenericTableSimpleSelectComponent,
                    inputs: {
                        modelValue: level,
                        options: this.selectOptionsForMatrixCell(def, role.id),
                        infoMsg: this.orgCellInfoForMatrix(def, role.id),
                    },
                    outputs: {
                        modelValueChange: (value: string | number) => {
                            void this.onMatrixCellChange(def, role.id, value as AccessLevel);
                        },
                    },
                };
            }
            return {
                id: def.rightName,
                name: `${def.displayName} ${def.category}`,
                data: { rightName: def.rightName },
                cells,
            };
        });
    }

    private async onByRoleAccessChange(def: RoleRights.RightWithRolesConfig, value: AccessLevel): Promise<void> {
        await this.store.setAccessLevelForSelectedRole(def.rightName, value);
        queueMicrotask(() => void this.roleTable?.refresh(true));
    }

    private async onMatrixCellChange(
        def: RoleRights.RightWithRolesConfig,
        roleId: number,
        value: AccessLevel,
    ): Promise<void> {
        await this.store.setAccessLevelForRole(def.rightName, roleId, value);
        queueMicrotask(() => void this.matrixTable?.refresh(true));
    }

    /** Representative level for the row “Apply to All” picker when visible columns disagree (most permissive). */
    private matrixApplyToAllDisplayLevel(def: RoleRights.RightWithRolesConfig, visibleRoleIds: number[]): AccessLevel {
        const levels = visibleRoleIds.map((id) => this.store.levelForRole(def.rightName, id));
        if (!levels.length) return AccessLevels.None;
        if (levels.every((l) => l === levels[0])) return levels[0];
        return levels.reduce((a, b) => (AccessLevelValue[a] >= AccessLevelValue[b] ? a : b));
    }

    private matrixApplyToAllLevelsMixed(def: RoleRights.RightWithRolesConfig, visibleRoleIds: number[]): boolean {
        const levels = visibleRoleIds.map((id) => this.store.levelForRole(def.rightName, id));
        if (levels.length <= 1) return false;
        return !levels.every((l) => l === levels[0]);
    }

    private matrixApplyToAllInfoMsg(def: RoleRights.RightWithRolesConfig, visibleRoleIds: number[]): string {
        return this.matrixApplyToAllLevelsMixed(def, visibleRoleIds)
            ? "Visible role columns differ; saving sets every visible column in this row to this level."
            : "";
    }

    private selectOptionsForMatrixApplyToAll(def: RoleRights.RightWithRolesConfig) {
        return accessLevelSelectOptionsForRight(def);
    }

    private async onMatrixApplyToAllChange(def: RoleRights.RightWithRolesConfig, value: AccessLevel): Promise<void> {
        const roleIds = this.visibleMatrixRoleColumnIds();
        if (!roleIds.length) return;
        if (this.orgId() != null) {
            await this.orgStore.applyLevelToVisibleRoleColumnsForRightPermissive(def.rightName, roleIds, value);
        } else {
            await this.baselineStore.applyLevelToVisibleRoleColumnsForRight(def.rightName, roleIds, value);
        }
        queueMicrotask(() => void this.matrixTable?.refresh(true));
    }

    private selectOptionsForMatrixCell(def: RoleRights.RightWithRolesConfig, roleId: number) {
        if (this.orgId() != null) {
            const eff = this.store.levelForRole(def.rightName, roleId);
            return accessLevelSelectOptionsNonDemoting(def, eff);
        }
        return accessLevelSelectOptionsForRight(def);
    }

    private orgCellInfoForMatrix(def: RoleRights.RightWithRolesConfig, roleId: number): string | undefined {
        if (this.orgId() == null) return undefined;
        const g = this.orgStore.globalLevelForRole(def.rightName, roleId);
        return `System default: ${accessLevelShortLabel(g)}`;
    }

    private selectOptionsForByRole(def: RoleRights.RightWithRolesConfig) {
        if (this.orgId() != null) {
            const eff = this.store.levelForSelectedRole(def.rightName);
            return accessLevelSelectOptionsNonDemoting(def, eff);
        }
        return accessLevelSelectOptionsForRight(def);
    }

    private orgCellInfoForByRole(def: RoleRights.RightWithRolesConfig): string | undefined {
        if (this.orgId() == null) return undefined;
        const roleId = this.store.selectedRoleId();
        if (roleId == null) return undefined;
        const g = this.orgStore.globalLevelForRole(def.rightName, roleId);
        return `System default: ${accessLevelShortLabel(g)}`;
    }

    private visibleMatrixRoleColumnIds(): number[] {
        const cols = this.matrixTable?.visibleColumns;
        if (!cols?.length) return [];
        return cols
            .map((c) => String(c.id))
            .filter((id) => id.startsWith("r_"))
            .map((id) => Number(id.slice(2)))
            .filter((id) => Number.isFinite(id) && id > 0);
    }
}

function accessLevelCellClass(level: AccessLevel): string {
    const base = "se-sec-access";
    if (level === AccessLevels.Full) return `${base} ${base}--full`;
    if (level === AccessLevels.Read) return `${base} ${base}--read`;
    if (level === AccessLevels.Edit) return `${base} ${base}--edit`;
    return `${base} ${base}--none`;
}
